import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Activity, CalendarCheck2, ArrowRight } from "lucide-react";
import { track } from "../lib/analytics";

export default function CheckinForm({ onCreated }) {
  const [weight, setWeight] = useState("");
  const [energy, setEnergy] = useState(7);
  const [mood, setMood] = useState(7);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [last, setLast] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/checkins");
        if (data.length) setLast(data[data.length - 1]);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkins", {
        weight_kg: Number(weight),
        energy: Number(energy),
        mood: Number(mood),
        notes,
      });
      track.checkinCreated(data);
      toast.success("Check-in saved! Keep going 🌱");
      setLast(data);
      setWeight("");
      setNotes("");
      if (onCreated) onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="checkin-card">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
          <CalendarCheck2 className="w-3.5 h-3.5" /> Weekly check-in
        </div>
        <h3 className="font-display text-xl text-emerald-950 mt-1">
          How's it going this week?
        </h3>
        {last ? (
          <div className="mt-2 text-xs text-emerald-900/60">
            Last: {Number(last.weight_kg).toFixed(1)} kg · energy {last.energy}/10 · mood {last.mood}/10
          </div>
        ) : null}

        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-1">
            <Label htmlFor="ckw" className="text-emerald-900">Weight (kg)</Label>
            <Input
              id="ckw"
              type="number"
              step="0.1"
              required
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="mt-1 rounded-xl border-emerald-200"
              placeholder="e.g. 76.4"
              data-testid="checkin-weight"
            />
          </div>
          <SliderField
            id="energy"
            label="Energy"
            value={energy}
            setValue={setEnergy}
            testid="checkin-energy"
          />
          <SliderField
            id="mood"
            label="Mood"
            value={mood}
            setValue={setMood}
            testid="checkin-mood"
          />
          <div className="sm:col-span-2">
            <Label htmlFor="ckn" className="text-emerald-900">Notes (optional)</Label>
            <Textarea
              id="ckn"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 rounded-xl border-emerald-200"
              placeholder="Any wins, struggles or observations…"
              data-testid="checkin-notes"
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-5"
              data-testid="checkin-submit"
            >
              <Activity className="w-4 h-4 mr-2" />
              {submitting ? "Saving…" : "Log this week"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function SliderField({ id, label, value, setValue, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-emerald-900">{label}</Label>
        <span className="text-sm font-semibold text-emerald-800">{value}/10</span>
      </div>
      <input
        id={id}
        type="range"
        min={1}
        max={10}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="mt-2 w-full accent-emerald-700"
        data-testid={testid}
      />
    </div>
  );
}
